import { useEffect, useState } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const Comments = ({ planetId, onNeedAuth }) => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/planets/${planetId}/comments`).then((r) => setItems(r.data)).catch(() => {});
  }, [planetId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) return onNeedAuth ? onNeedAuth() : toast("Sign in to leave a log entry");
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/planets/${planetId}/comments`, { text });
      setItems((it) => [...it, data]);
      setText("");
      toast.success("Log entry recorded");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="glass rounded-xl p-6 sm:p-8" data-testid="comments-section">
      <div className="flex items-baseline justify-between mb-6">
        <h3 className="font-display text-base sm:text-lg font-semibold uppercase tracking-wide text-slate-50">Explorer log</h3>
        <span className="label-mono" data-testid="comments-count">{items.length} entries</span>
      </div>

      <ul className="space-y-4 mb-8" data-testid="comments-list">
        {items.length === 0 && <li className="text-sm text-slate-500" data-testid="comments-empty">No log entries yet. Be the first to leave a note for the next traveler.</li>}
        {items.map((c) => (
          <li key={c.id} className="border-l-2 border-cyan-500/40 pl-4" data-testid="comment-item">
            <div className="flex items-center gap-3 mb-1">
              <span className="font-mono text-xs text-cyan-300">{c.user_name}</span>
              <span className="label-mono !text-[0.6rem]">{new Date(c.created_at).toLocaleString()}</span>
            </div>
            <p className="text-sm text-slate-300 leading-relaxed">{c.text}</p>
          </li>
        ))}
      </ul>

      <form onSubmit={submit} className="flex gap-3">
        <input className="input-hud flex-1" value={text} onChange={(e) => setText(e.target.value)} maxLength={600}
          placeholder={user ? "Leave a log entry for the next explorer…" : "Sign in to leave a log entry"} data-testid="comment-input" />
        <button type="submit" disabled={busy} className="btn-primary !px-4" data-testid="comment-submit-button">
          <Send className="h-4 w-4" />
        </button>
      </form>
    </section>
  );
};
